import { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, Plus, Pencil, Trash2, Search, Building2, Mail, Eye, EyeOff, UserCheck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { prenom: "", nom: "", email: "", password: "" };

function SkeletonCard() {
  return (
    <div className="skeleton-card flex items-center gap-4">
      <div className="skeleton w-12 h-12 rounded-xl flex-shrink-0" />
      <div className="flex-1">
        <div className="skeleton h-4 w-1/2 mb-2" />
        <div className="skeleton h-3 w-2/3" />
      </div>
      <div className="skeleton h-8 w-20 rounded-lg" />
    </div>
  );
}

export default function Equipe() {
  const [ouvriers, setOuvriers] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useLanguage();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [ouvriersRes, chantiersRes] = await Promise.all([
        axios.get(`${API}/users/ouvriers`),
        axios.get(`${API}/chantiers`)
      ]);
      setOuvriers(ouvriersRes.data);
      setChantiers(chantiersRes.data);
    } catch (e) {
      toast.error(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const getAssignedChantiers = (ouvrierId) =>
    chantiers.filter(c => c.ouvriers_assignes?.includes(ouvrierId));

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEdit = (ouv) => {
    setEditing(ouv);
    setForm({ prenom: ouv.prenom, nom: ouv.nom, email: ouv.email, password: "" });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { prenom: form.prenom, nom: form.nom, email: form.email };
        if (form.password.trim()) payload.password = form.password;
        await axios.put(`${API}/users/${editing.id}`, payload);
        toast.success(t('ouvrierUpdated'));
      } else {
        if (!form.password.trim()) { toast.error(t('errorGeneric')); setSubmitting(false); return; }
        await axios.post(`${API}/users/ouvriers`, { ...form, role: "ouvrier" });
        toast.success(t('ouvrierCreated'));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorSaving'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ouv) => {
    if (!window.confirm(t('confirmDeleteOuvrier'))) return;
    try {
      await axios.delete(`${API}/users/${ouv.id}`);
      toast.success(t('ouvrierDeleted'));
      fetchData();
    } catch (e) {
      toast.error(t('errorGeneric'));
    }
  };

  const filtered = ouvriers.filter(o =>
    `${o.prenom} ${o.nom} ${o.email}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-5 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div><div className="skeleton h-8 w-32 mb-2" /><div className="skeleton h-4 w-48" /></div>
          <div className="skeleton h-10 w-40 rounded-lg" />
        </div>
        <div className="space-y-3">{[1,2,3].map(i => <SkeletonCard key={i} />)}</div>
      </div>
    );
  }

  return (
    <div data-testid="equipe-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1E293B] uppercase tracking-tight font-['Barlow_Condensed']">
            {t('equipeTitle')}
          </h1>
          <p className="text-[#94A3B8] mt-1 text-sm">{t('equipeSubtitle')}</p>
        </div>
        <Button
          data-testid="btn-new-ouvrier"
          onClick={openCreate}
          className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg uppercase tracking-wide text-sm font-medium shadow-md shadow-blue-200 btn-scale"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('newOuvrier')}
        </Button>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="bg-white border-[#E2E8F0]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-[#3B82F6]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1E293B]">{ouvriers.length}</p>
              <p className="text-xs text-[#94A3B8]">{t('ouvrierRole')}s</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E2E8F0]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1E293B]">
                {ouvriers.filter(o => getAssignedChantiers(o.id).length > 0).length}
              </p>
              <p className="text-xs text-[#94A3B8]">Assignés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E2E8F0] hidden md:block">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#F97316]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#1E293B]">
                {chantiers.filter(c => c.statut === 'en_cours').length}
              </p>
              <p className="text-xs text-[#94A3B8]">{t('inProgress')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      {ouvriers.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="search-ouvriers"
            placeholder={t('searchOuvrier')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-white border-[#E2E8F0] text-[#1E293B] rounded-lg shadow-sm"
          />
        </div>
      )}

      {/* Workers list */}
      {filtered.length === 0 ? (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-[#CBD5E1]" />
            <h3 className="text-xl font-semibold text-[#1E293B] mb-2">{t('noOuvriersYet')}</h3>
            <p className="text-[#94A3B8] mb-4">{t('addFirstOuvrier')}</p>
            <Button onClick={openCreate} className="bg-[#3B82F6] text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" />{t('newOuvrier')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(ouv => {
            const assigned = getAssignedChantiers(ouv.id);
            return (
              <Card
                key={ouv.id}
                data-testid={`ouvrier-card-${ouv.id}`}
                className="bg-white border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {ouv.prenom?.[0]?.toUpperCase()}{ouv.nom?.[0]?.toUpperCase()}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-[#1E293B]">{ouv.prenom} {ouv.nom}</h3>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full border border-blue-100 font-medium">
                          👷 {t('ouvrierRole')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-[#64748B] text-sm mt-0.5">
                        <Mail className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
                        <span className="truncate">{ouv.email}</span>
                      </div>
                      {/* Assigned chantiers */}
                      {assigned.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {assigned.map(c => (
                            <span key={c.id} className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs rounded-full border border-emerald-100 flex items-center gap-1">
                              <Building2 className="w-2.5 h-2.5" />{c.nom}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-[#94A3B8] mt-1">Aucun chantier assigné</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        data-testid={`btn-edit-ouvrier-${ouv.id}`}
                        onClick={() => openEdit(ouv)}
                        className="p-2 text-[#94A3B8] hover:text-[#3B82F6] hover:bg-blue-50 rounded-lg transition-colors"
                        title={t('edit')}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`btn-delete-ouvrier-${ouv.id}`}
                        onClick={() => handleDelete(ouv)}
                        className="p-2 text-[#94A3B8] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#1E293B] max-w-md shadow-xl" aria-describedby="ouvrier-form-desc">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase font-['Barlow_Condensed'] text-[#1E293B]">
              {editing ? t('editOuvrier') : t('newOuvrier')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium text-sm">{t('firstName')} *</Label>
                <Input
                  data-testid="input-ouvrier-prenom"
                  required
                  value={form.prenom}
                  onChange={e => setForm({...form, prenom: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                  placeholder="Jean"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#475569] font-medium text-sm">{t('lastName')} *</Label>
                <Input
                  data-testid="input-ouvrier-nom"
                  required
                  value={form.nom}
                  onChange={e => setForm({...form, nom: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                  placeholder="Dupont"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium text-sm">{t('emailField')} *</Label>
              <Input
                data-testid="input-ouvrier-email"
                type="email"
                required
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg"
                placeholder="jean.dupont@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#475569] font-medium text-sm">
                {editing ? t('newPasswordHint') : `${t('passwordField')} *`}
              </Label>
              <div className="relative">
                <Input
                  data-testid="input-ouvrier-password"
                  type={showPassword ? "text" : "password"}
                  required={!editing}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  className="bg-[#F8FAFC] border-[#E2E8F0] text-[#1E293B] rounded-lg pr-10"
                  placeholder={editing ? "••••••••" : "Minimum 4 caractères"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#1E293B] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {editing && (
                <p className="text-xs text-[#94A3B8]">{t('newPasswordHint')}</p>
              )}
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg">
                {t('cancel')}
              </Button>
              <Button
                data-testid="btn-submit-ouvrier"
                type="submit"
                disabled={submitting}
                className="bg-[#3B82F6] hover:bg-blue-600 text-white rounded-lg shadow-md shadow-blue-200 btn-scale"
              >
                {submitting
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : editing ? t('save') : t('create')
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
